import React, { useEffect, useState } from "react";
import Input from "../../common/input";
import { PlusIcon, Spinner } from "../../../assets/icons/icons";

export default function AvatarFormDialog({
  isOpen,
  onClose,
  handleSave,
  isPending,
  prompt: parentPrompt = "",
}) {
  const [formData, setFormData] = useState({
    name: "",
    is_premium: false,
    prompt: parentPrompt || "",
    thumbnailImg: "",
  });

  useEffect(() => {
    if (isOpen) {
      if (!formData.prompt) {
        setFormData((prev) => ({
          ...prev,
          prompt: parentPrompt || "", // Ensure prompt is updated when dialog is opened
        }));
      }
    }
  }, [isOpen, parentPrompt]); // Add both `isOpen` and `parentPrompt` as dependencies

  const [errors, setErrors] = useState({});
  const [imgPreview, setImgPreview] = useState(null);

  const closeDialog = () => onClose();

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    // Prevent number at the start for the 'name' field
    if (name === "name" && value.length === 1 && /\d/.test(value[0])) {
      return; // Skip updating the state if the first character is a number
    }

    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for the current field
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleThumbnailImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setImgPreview(url);
      setFormData((prev) => ({
        ...prev,
        thumbnailImg: file,
      }));

      // Clear thumbnail image error
      setErrors((prev) => ({ ...prev, thumbnailImg: "" }));
    }
  };

  const validateFields = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required.";
    }
    if (!formData.prompt.trim()) {
      newErrors.prompt = "Prompt is required.";
    }
    if (!formData.thumbnailImg) {
      newErrors.thumbnailImg = "Thumbnail image is required.";
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateFields();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    handleSave(formData, closeDialog);
  };

  return (
    <>
      {isOpen && (
        <div className="relative z-50">
          <div
            className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-[30px] transition-opacity"
            aria-hidden="true"
          ></div>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white text-left transition-all sm:my-8 sm:w-full sm:max-w-[800px]">
                <form onSubmit={handleSubmit}>
                  <div className="p-[24px]">
                    <h2 className="mb-[20px] text-left font-outfit text-[20px] font-medium leading-[25.2px] text-[#222222]">
                      Create New
                    </h2>
                    <div className="mb-[40px] grid grid-flow-row grid-cols-2 gap-x-[40px] gap-y-[20px]">
                      <Input
                        label="Name"
                        value={formData.name}
                        onChange={handleChange}
                        name="name"
                        type="text"
                        required
                        width="w-[356px]"
                        error={errors.name}
                        disabled={isPending}
                      />

                      <Input
                        label="Prompt"
                        value={formData.prompt}
                        onChange={handleChange}
                        name="prompt"
                        type="text"
                        width="w-[356px]"
                        required
                        error={errors.prompt}
                        disabled={isPending}
                      />

                      <Input
                        label="Premium"
                        checked={formData.is_premium}
                        onChange={handleChange}
                        name="is_premium"
                        type="checkbox"
                        width="w-[20px]"
                        disabled={isPending}
                      />

                      <div className="w-full max-w-[356px]">
                        <p className="mb-3 w-full text-left font-outfit text-[14px] font-medium leading-[17.64px] text-[#666666]">
                          Thumbnail Image
                        </p>
                        <label
                          htmlFor="thumbnail-image"
                          className="relative block w-full rounded-[10px] border border-[#0075FF1A] py-[13px] text-center font-outfit text-[14px] font-medium leading-[17.64px] text-[#0075FF]"
                        >
                          Upload Thumbnail
                          <div className="absolute left-[5px] top-1/2 flex h-[34px] w-[34px] -translate-y-1/2 items-center justify-center rounded-[6px] bg-[#0075FF1A]">
                            <PlusIcon width={11.67} height={11.67} />
                          </div>
                        </label>

                        <input
                          type="file"
                          accept="image/*"
                          id="thumbnail-image"
                          className="hidden"
                          onChange={handleThumbnailImage}
                          disabled={isPending}
                        />
                        {imgPreview && (
                          <img
                            src={imgPreview}
                            alt="Thumbnail preview"
                            className="mt-2 h-12 w-12 rounded-full object-cover"
                          />
                        )}
                        {errors.thumbnailImg && (
                          <p className="mt-2 text-sm text-red-500">
                            {errors.thumbnailImg}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-row justify-end gap-[30px] px-6">
                      <button
                        type="button"
                        className="rounded-[10px] bg-[#F5F5F5] px-[46px] py-[12px] text-center font-outfit text-[16px] font-medium leading-[20.16px] text-[#222222]"
                        onClick={closeDialog}
                        disabled={isPending}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="rounded-[10px] bg-[#0075FF] px-[46px] py-[12px] text-center font-outfit text-[16px] font-medium leading-[20.16px] text-white"
                        disabled={isPending}
                      >
                        {isPending ? (
                          <div className="flex flex-row items-center justify-center gap-1">
                            <Spinner />
                            <p> Saving</p>
                          </div>
                        ) : (
                          "Done"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
